import { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { SiatCompanyModal } from './SiatCompanyModal';
import { SiatBranchModal } from './SiatBranchModal';
import type { BranchModalMode } from './SiatBranchModal';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';

export const FacturacionSiatTab = () => {
    const [siatData, setSiatData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);

    const [isBranchModalOpen, setIsBranchModalOpen] = useState(false);
    const [editingBranch, setEditingBranch] = useState<any>(null);
    const [modalMode, setModalMode] = useState<BranchModalMode>('create_branch');

    // ESTADO PARA EL MODAL DE CONFIRMACIÓN DE BAJA
    const [deleteModal, setDeleteModal] = useState<{
        isOpen: boolean;
        type: 'pos' | 'branch' | null;
        id: number | null;
        isProcessing: boolean;
    }>({ isOpen: false, type: null, id: null, isProcessing: false });

    const fetchSiatStatus = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get<any>('SiatStatus');
            let resBody = response.data?.statusCode !== undefined ? response.data : response;

            if (resBody.hasError) {
                throw new Error(resBody.resultError?.message || "Error al cargar la configuración SIAT.");
            }

            setSiatData(resBody.data);
        } catch (err: any) {
            console.error("Error fetching SiatStatus:", err);
            setError(err.message || "Ocurrió un error de conexión.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSiatStatus();
    }, []);

    // --- MANEJADORES DE GUARDADO ---

    const handleSaveCompany = async (payload: any, isEdit: boolean) => {
        try {
            let res = isEdit
                ? await apiClient.put<any>('SiatCompany', payload)
                : await apiClient.post<any>('SiatCompany', payload);

            if (res.data?.hasError) throw new Error(res.data.resultError?.message);
            await fetchSiatStatus();
        } catch (err: any) {
            alert(err.message || "Ocurrió un error al guardar la empresa.");
            throw err;
        }
    };

    // ENRUTADOR DE ENDPOINTS SEGÚN EL MODO
    const handleSaveBranch = async (payload: any, mode: BranchModalMode, ids: { branchId?: number, posId?: number }) => {
        try {
            let res;
            if (mode === 'add_pos' && ids.branchId) {
                res = await apiClient.post<any>(`CompanyBranch/${ids.branchId}/PointOfSale`, payload);
            } else if (mode === 'edit_branch' && ids.branchId) {
                res = await apiClient.put<any>(`CompanyBranch/${ids.branchId}`, payload);
            } else if (mode === 'edit_pos' && ids.posId) {
                res = await apiClient.put<any>(`CompanyBranch/PointOfSale/${ids.posId}`, payload);
            } else {
                res = await apiClient.post<any>('CompanyBranch', payload);
            }

            if (res.data?.hasError) throw new Error(res.data.resultError?.message);
            await fetchSiatStatus();
        } catch (err: any) {
            alert(err.message || "Ocurrió un error al procesar la solicitud.");
            throw err;
        }
    };

    // DAR DE BAJA UN SOLO PUNTO DE VENTA
    const handleDeletePos = async (siatPointOfSaleId: number) => {
        if (!window.confirm("¿Está seguro de que desea dar de baja este Punto de Venta?")) return;
        try {
            const res = await apiClient.delete<any>(`CompanyBranch/PointOfSale/${siatPointOfSaleId}`);
            if (res.data?.hasError) throw new Error(res.data.resultError?.message);
            await fetchSiatStatus();
        } catch (err: any) {
            alert(err.message || "Ocurrió un error al dar de baja el punto de venta.");
        }
    };

    // DAR DE BAJA SUCURSAL COMPLETA
    const handleDeleteFullBranch = async (siatBranchId: number) => {
        if (!window.confirm("¿Está seguro de que desea dar de baja toda la Sucursal (incluyendo sus puntos de venta)?")) return;
        try {
            const res = await apiClient.delete<any>(`CompanyBranch/${siatBranchId}`);
            if (res.data?.hasError) throw new Error(res.data.resultError?.message);
            await fetchSiatStatus();
        } catch (err: any) {
            alert(err.message || "Ocurrió un error al dar de baja la sucursal.");
        }
    };

    // --- RENDERIZADO MIENTRAS CARGA ---
    if (isLoading && !siatData) {
        return (
            <div className="flex flex-col items-center justify-center w-full min-h-[400px]">
                <Loader2 className="w-8 h-8 mb-2 animate-spin text-[#4f6df5]" />
                <span className="text-sm font-semibold text-gray-500">Cargando configuración SIAT...</span>
            </div>
        );
    }

    if (error && !siatData) {
        return (
            <div className="flex items-center justify-center w-full min-h-[400px]">
                <div className="p-4 text-sm text-red-700 bg-red-100 rounded-lg">{error}</div>
            </div>
        );
    }

    const company = siatData?.company;
    const rows = siatData?.rows || [];

    // --- FUNCIONES PARA ABRIR EL MODAL ---
    const confirmDeletePos = (siatPointOfSaleId: number) => {
        setDeleteModal({ isOpen: true, type: 'pos', id: siatPointOfSaleId, isProcessing: false });
    };

    const confirmDeleteBranch = (siatBranchId: number) => {
        setDeleteModal({ isOpen: true, type: 'branch', id: siatBranchId, isProcessing: false });
    };

    // FUNCIÓN QUE EJECUTA LA BAJA
    const executeDelete = async () => {
        if (!deleteModal.id || !deleteModal.type) return;

        setDeleteModal(prev => ({ ...prev, isProcessing: true }));

        try {
            let res;
            if (deleteModal.type === 'pos') {
                res = await apiClient.delete<any>(`CompanyBranch/PointOfSale/${deleteModal.id}`);
            } else {
                res = await apiClient.delete<any>(`CompanyBranch/${deleteModal.id}`);
            }

            if (res.data?.hasError) throw new Error(res.data.resultError?.message);

            await fetchSiatStatus();
            setDeleteModal({ isOpen: false, type: null, id: null, isProcessing: false });
        } catch (err: any) {
            alert(err.message || "Ocurrió un error al dar de baja el registro.");
            setDeleteModal(prev => ({ ...prev, isProcessing: false }));
        }
    };

    return (
        <div className="flex flex-col w-full text-gray-800 bg-white">
            <div className="mb-6 flex justify-between items-start">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                        Consola de Configuración SIAT (Facturación en Línea)
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Parámetros institucionales y control de vigencia de credenciales por sucursal en Bolivia
                    </p>
                </div>

                {siatData && !siatData.isConfigurationComplete && (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-orange-700 bg-orange-100 rounded-md">
                        <AlertCircle size={16} />
                        <span>Faltan configuraciones: {siatData.missingConfigurationFields?.join(', ')}</span>
                    </div>
                )}
            </div>

            {/* SECCIÓN 1: Datos de Empresa */}
            <div className="p-6 mb-8 border rounded-lg bg-slate-50 border-slate-100 relative">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-sm font-semibold tracking-wider text-blue-900 uppercase">
                        1. Datos de Empresa y Credenciales Globales
                    </h3>
                    <button
                        onClick={() => setIsCompanyModalOpen(true)}
                        className="text-sm font-medium text-blue-600 hover:underline"
                    >
                        {company ? '[Editar Empresa]' : '[Registrar Empresa]'}
                    </button>
                </div>

                {company ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
                        <div>
                            <p className="mb-1 text-xs text-gray-500">Razón Social:</p>
                            <p className="text-sm font-medium">{company.issuerBusinessName || '-'}</p>
                        </div>
                        <div>
                            <p className="mb-1 text-xs text-gray-500">NIT Emisor:</p>
                            <p className="text-sm font-medium">{company.issuerNit || '-'}</p>
                        </div>
                        <div>
                            <p className="mb-1 text-xs text-gray-500">Certificado Firma Digital (ADSIB):</p>
                            <p className={`text-sm font-medium ${company.hasValidDigitalSignature ? 'text-emerald-600' : 'text-red-600'}`}>
                                {company.digitalSignatureStatus} {company.digitalSignatureExpiration && `(Vence: ${company.digitalSignatureExpiration})`}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1 text-xs text-gray-500">Token SIAT General:</p>
                            <p className="text-sm font-medium text-gray-600">
                                {company.hasSiatEnvironmentToken ? `Configurado [${company.siatEnvironmentTokenMasked}]` : 'No configurado'}
                            </p>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 italic">No hay datos de empresa registrados. Por favor, registre la empresa primero.</p>
                )}
            </div>

            {/* SECCIÓN 2: Tabla de Sucursales */}
            <div className="mb-4">
                <h3 className="mb-4 text-sm font-semibold tracking-wider text-blue-900 uppercase">
                    2. Control de Sucursales, Puntos de Venta y Cronograma CUIS / CUFD
                </h3>

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-[#1e3a8a] text-white">
                            <tr>
                                <th className="px-4 py-3 font-medium">Cód. Suc.</th>
                                <th className="px-4 py-3 font-medium">Nombre Sucursal / Dirección</th>
                                <th className="px-4 py-3 font-medium">Pto. Venta</th>
                                <th className="px-4 py-3 font-medium">Tipo Punto Venta</th>
                                <th className="px-4 py-3 font-medium">CUIS Activo</th>
                                <th className="px-4 py-3 font-medium">Vencimiento CUIS</th>
                                <th className="px-4 py-3 font-medium">Estado SIAT</th>
                                <th className="px-4 py-3 font-medium">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {rows.length === 0 ? (
                                <tr className="hover:bg-gray-50">
                                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                                        No hay sucursales ni puntos de venta registrados.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row: any, index: number) => {
                                    const hasPoS = row.siatPointOfSaleId != null;

                                    return (
                                        <tr key={hasPoS ? `pos-${row.siatPointOfSaleId}` : `branch-${row.siatBranchId}-${index}`} className="hover:bg-gray-50">
                                            <td className="px-4 py-4">{row.branchCode}</td>
                                            <td className="px-4 py-4">
                                                <p className="font-medium">{row.branchName}</p>
                                                <p className="text-xs text-gray-500">{row.address}</p>
                                            </td>

                                            {/* Si no tiene PoS, mostramos guiones. Si tiene, los datos. */}
                                            <td className="px-4 py-4">{hasPoS ? row.pointOfSaleCode : '-'}</td>
                                            <td className="px-4 py-4">{hasPoS ? row.pointOfSaleTypeDescription : '-'}</td>
                                            <td className="px-4 py-4 text-gray-600">{hasPoS ? row.cuis : '-'}</td>
                                            <td className={`px-4 py-4 ${row.hasValidCuis ? 'text-emerald-600' : 'text-red-600'}`}>
                                                {hasPoS ? row.cuisExpirationDate : '-'}
                                            </td>
                                            <td className="px-4 py-4">
                                                {hasPoS ? (
                                                    <span className={`px-2 py-1 text-xs rounded font-medium ${row.siatStatus === 'ONLINE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                                        {row.siatStatus}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-gray-400 italic">Sin Puntos</span>
                                                )}
                                            </td>

                                            {/* ACCIONES */}
                                            <td className="px-4 py-4 min-w-[250px] flex flex-wrap gap-2">
                                                {/* ACCIONES DE SUCURSAL (Siempre visibles) */}
                                                <button
                                                    onClick={() => {
                                                        setEditingBranch(row);
                                                        setModalMode('add_pos');
                                                        setIsBranchModalOpen(true);
                                                    }}
                                                    className="text-emerald-600 hover:text-emerald-800 hover:underline font-medium"
                                                    title="Agregar Punto de Venta a esta sucursal"
                                                >
                                                    [+ Pto. Venta]
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setEditingBranch(row);
                                                        setModalMode('edit_branch');
                                                        setIsBranchModalOpen(true);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                                >
                                                    [Editar Suc.]
                                                </button>
                                                <button
                                                    onClick={() => confirmDeleteBranch(row.siatBranchId)}
                                                    className="text-red-600 hover:text-red-800 hover:underline"
                                                    title="Dar de baja toda la sucursal y sus puntos"
                                                >
                                                    [Baja Suc.]
                                                </button>

                                                {/* ACCIONES DE PUNTO DE VENTA (Solo si existe el PoS) */}
                                                {hasPoS && (
                                                    <>
                                                        <button
                                                            onClick={() => {
                                                                setEditingBranch(row);
                                                                setModalMode('edit_pos');
                                                                setIsBranchModalOpen(true);
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800 hover:underline"
                                                        >
                                                            [Editar Pto.]
                                                        </button>
                                                        <button
                                                            onClick={() => confirmDeletePos(row.siatPointOfSaleId)}
                                                            className="text-red-600 hover:text-red-800 hover:underline"
                                                            title="Dar de baja solo este punto de venta"
                                                        >
                                                            [Baja Pto.]
                                                        </button>
                                                    </>
                                                )}
                                            </td>

                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex flex-col items-start gap-4 mt-4">
                <button
                    disabled={!company}
                    onClick={() => {
                        setEditingBranch(null);
                        setModalMode('create_branch');
                        setIsBranchModalOpen(true);
                    }}
                    className="bg-[#1e3a8a] hover:bg-blue-900 disabled:bg-gray-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                    + Registrar Nueva Sucursal
                </button>
                <p className="text-xs italic text-gray-500">
                    * Monitoreo Automático del Sistema JOM: El backend realiza la renovación automática del código CUFD cada 24 horas para cada Punto de Venta activo.
                </p>
            </div>

            {/* MODALES */}
            <SiatCompanyModal
                isOpen={isCompanyModalOpen}
                onClose={() => setIsCompanyModalOpen(false)}
                onSave={handleSaveCompany}
                initialData={company}
            />

            <SiatBranchModal
                isOpen={isBranchModalOpen}
                onClose={() => setIsBranchModalOpen(false)}
                onSave={handleSaveBranch}
                initialData={editingBranch}
                mode={modalMode}
            />

            <ConfirmDeleteModal
                isOpen={deleteModal.isOpen}
                type={deleteModal.type}
                isProcessing={deleteModal.isProcessing}
                onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={executeDelete}
            />

        </div>
    );
};